import { FormEvent, useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Loader2,
  Lock,
  LogOut,
  MessageCircle,
  Reply,
  Send,
  Trash2,
  X,
} from "lucide-react";
import "@edgespark/client/styles.css";
import { client, BlogPost } from "../api/client";

function formatDate(ts: number) {
  return new Date(ts).toLocaleString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyBusy, setReplyBusy] = useState<number | null>(null);

  const [showAuth, setShowAuth] = useState(false);
  const authRef = useRef<HTMLDivElement>(null);
  const authRendered = useRef(false);

  async function loadPosts() {
    try {
      const res = await client.api.fetch("/api/public/posts");
      const json = await res.json();
      setPosts(json.data ?? []);
      setIsAdmin(Boolean(json.isAdmin));
    } catch (err) {
      console.error("Failed to load posts", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPosts();
  }, []);

  // Render managed auth UI inside the modal (once)
  useEffect(() => {
    if (!showAuth || authRendered.current || !authRef.current) return;
    authRendered.current = true;
    client.auth.renderAuthUI(authRef.current, {
      labels: {
        signIn: {
          title: "Autentificare administrator",
          subtitle: "Conectează-te pentru a răspunde mesajelor",
          loginButton: "Intră în cont",
          forgotPassword: "Ai uitat parola?",
          signUpPrompt: "",
          toggleToSignUp: "",
        },
      },
      onLogin: () => {
        setShowAuth(false);
        authRendered.current = false;
        setIsAdmin(true);
        loadPosts();
      },
    });
  }, [showAuth]);

  async function handlePost(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    setSubmitting(true);
    setNotice(null);
    try {
      const res = await client.api.fetch("/api/public/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), message: message.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setNotice(j.error || "A apărut o eroare. Încearcă din nou.");
        return;
      }
      setName("");
      setMessage("");
      setNotice("Mesajul tău a fost publicat. Mulțumim!");
      await loadPosts();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReply(postId: number) {
    const text = (replyDrafts[postId] || "").trim();
    if (!text) return;
    setReplyBusy(postId);
    try {
      const res = await client.api.fetch(`/api/posts/${postId}/replies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) {
        setReplyDrafts((d) => ({ ...d, [postId]: "" }));
        await loadPosts();
      }
    } finally {
      setReplyBusy(null);
    }
  }

  async function deletePost(postId: number) {
    const res = await client.api.fetch(`/api/posts/${postId}`, { method: "DELETE" });
    if (res.ok) await loadPosts();
  }

  async function deleteReply(replyId: number) {
    const res = await client.api.fetch(`/api/replies/${replyId}`, { method: "DELETE" });
    if (res.ok) await loadPosts();
  }

  async function handleLogout() {
    await client.auth.signOut();
    setIsAdmin(false);
    loadPosts();
  }

  return (
    <section id="blog" className="blog-section section-pad page-offset">
      <div className="section-heading">
        <div>
          <p className="kicker">Comunitatea vorbește</p>
          <h2>Blog &amp;<br /><span>mesaje</span></h2>
        </div>
        <p className="heading-copy">
          Lasă un mesaj public, întreabă-ne orice sau spune-ne părerea ta. Îți răspundem direct aici, sub mesajul tău.
        </p>
      </div>

      <div className="blog-admin-bar">
        {isAdmin ? (
          <button className="admin-chip" onClick={handleLogout}>
            <LogOut size={15} /> Ieși din contul de admin
          </button>
        ) : (
          <button className="admin-chip ghost" onClick={() => setShowAuth(true)}>
            <Lock size={14} /> Admin
          </button>
        )}
      </div>

      <div className="blog-layout">
        <form className="blog-form" onSubmit={handlePost}>
          <h3><MessageCircle size={18} /> Scrie un mesaj</h3>
          <label>
            Numele tău
            <input
              type="text"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Andrei"
              required
            />
          </label>
          <label>
            Mesajul tău
            <textarea
              value={message}
              maxLength={2000}
              rows={5}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrie aici ce vrei să ne transmiți..."
              required
            />
          </label>
          <button type="submit" className="blog-submit" disabled={submitting}>
            {submitting ? <Loader2 className="spin" size={18} /> : <Send size={17} />}
            Publică mesajul
          </button>
          {notice && <p className="blog-notice">{notice}</p>}
        </form>

        <div className="blog-feed">
          {loading ? (
            <div className="blog-empty"><Loader2 className="spin" size={22} /> Se încarcă mesajele...</div>
          ) : posts.length === 0 ? (
            <div className="blog-empty">Încă nu există mesaje. Fii primul care scrie!</div>
          ) : (
            posts.map((post) => (
              <article className="blog-card" key={post.id}>
                <header>
                  <div className="blog-avatar">{post.name.charAt(0).toUpperCase()}</div>
                  <div className="blog-meta">
                    <strong>{post.name}</strong>
                    <span>{formatDate(post.createdAt)}</span>
                  </div>
                  {isAdmin && (
                    <button className="icon-danger" onClick={() => deletePost(post.id)} aria-label="Șterge mesajul">
                      <Trash2 size={16} />
                    </button>
                  )}
                </header>
                <p className="blog-text">{post.message}</p>

                {post.replies.length > 0 && (
                  <div className="blog-replies">
                    {post.replies.map((r) => (
                      <div className="blog-reply" key={r.id}>
                        <div className="reply-badge"><Reply size={13} /> {r.authorName}</div>
                        <p>{r.message}</p>
                        <span className="reply-date">{formatDate(r.createdAt)}</span>
                        {isAdmin && (
                          <button className="icon-danger tiny" onClick={() => deleteReply(r.id)} aria-label="Șterge răspunsul">
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {isAdmin && (
                  <div className="blog-reply-form">
                    <input
                      type="text"
                      value={replyDrafts[post.id] || ""}
                      onChange={(e) => setReplyDrafts((d) => ({ ...d, [post.id]: e.target.value }))}
                      placeholder="Scrie un răspuns..."
                      onKeyDown={(e) => e.key === "Enter" && handleReply(post.id)}
                    />
                    <button onClick={() => handleReply(post.id)} disabled={replyBusy === post.id}>
                      {replyBusy === post.id ? <Loader2 className="spin" size={16} /> : <ArrowUpRight size={16} />}
                    </button>
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>

      {showAuth && (
        <div className="auth-overlay" onClick={() => { setShowAuth(false); authRendered.current = false; }}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="auth-close"
              onClick={() => { setShowAuth(false); authRendered.current = false; }}
              aria-label="Închide"
            >
              <X size={18} />
            </button>
            <div ref={authRef} className="auth-container" />
          </div>
        </div>
      )}
    </section>
  );
}
